import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { readSessionFlag, writeSessionFlag, PROMPT_SHOWN_KEY, SURVEY_OPEN_KEY } from '@/lib/purchases-eligibility';

// --- Constants ---

export const FEEDBACK_TIME_KEY = 'feedback_accumulated_time';
export const FEEDBACK_SESSION_KEY = 'feedback_session_start';
export const MIN_ACCUMULATED_MS = 1_800_000; // 30 minutes
export const DISMISS_RETRY_MS = 24 * 60 * 60 * 1000; // 24 hours
export const MAX_DISMISS_COUNT = 2;
export const EARLY_SUPPORTER_BADGE_ID = 'f33db4c0-ea51-4001-b001-000000000001';

// --- Interfaces ---

export interface FeedbackData {
  satisfaction: string;
  purchase_experience: string;
  purchase_details?: string;
  feature_requests: string[];
  open_feedback?: string;
  word_count: number;
}

// --- Time tracking helpers (exported for testing) ---

export function getAccumulatedTime(): number {
  try {
    const raw = localStorage.getItem(FEEDBACK_TIME_KEY);
    const value = raw ? parseInt(raw, 10) : 0;
    return Math.max(0, isNaN(value) ? 0 : value);
  } catch {
    return 0;
  }
}

export function setAccumulatedTime(ms: number): void {
  try {
    localStorage.setItem(FEEDBACK_TIME_KEY, String(Math.max(0, Math.floor(ms))));
  } catch {
    // localStorage may be full or unavailable
  }
}

export function startSession(): void {
  try {
    localStorage.setItem(FEEDBACK_SESSION_KEY, String(Date.now()));
  } catch {
    // localStorage may be full or unavailable
  }
}

export function updateAccumulatedTime(): number {
  const now = Date.now();
  const sessionStartRaw = localStorage.getItem(FEEDBACK_SESSION_KEY);
  const sessionStart = sessionStartRaw ? parseInt(sessionStartRaw, 10) : now;
  const elapsed = Math.max(0, now - sessionStart);
  const accumulated = getAccumulatedTime() + elapsed;
  setAccumulatedTime(accumulated);
  // Reset session start to now
  startSession();
  return accumulated;
}

// --- Hook ---

export function useFeedbackSurvey() {
  const { user } = useAuth();
  const [shouldShowInvite, setShouldShowInvite] = useState(false);
  const [shouldShowSurvey, setShouldShowSurvey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const checkedRef = useRef(false);

  // Track session time: start on mount, save on unmount/close
  useEffect(() => {
    startSession();

    const handleBeforeUnload = () => updateAccumulatedTime();
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      updateAccumulatedTime();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Manual trigger from Profile "Send Feedback" row — bypasses eligibility checks
  // (user is explicitly asking to leave feedback). Listens for a global event so
  // any component can open the survey without lifting this hook's state into context.
  useEffect(() => {
    const handleForceOpen = () => {
      setShouldShowInvite(false);
      setShouldShowSurvey(true);
    };
    window.addEventListener('open-feedback-survey', handleForceOpen);
    return () => window.removeEventListener('open-feedback-survey', handleForceOpen);
  }, []);

  // Eligibility check — runs once per mount
  useEffect(() => {
    if (checkedRef.current) return;
    if (!user?.id) return;
    checkedRef.current = true;

    const checkEligibility = async () => {
      try {
        // Step 1: Check accumulated time
        const totalTime = updateAccumulatedTime();
        logger.log('[Feedback] Eligibility:', { totalTime, threshold: MIN_ACCUMULATED_MS });

        if (totalTime < MIN_ACCUMULATED_MS) return;

        // Step 2: Check for real user actions (parallel queries)
        const [scriptUsageResult, chatMessagesResult, lessonProgressResult] = await Promise.all([
          supabase
            .from('script_usage')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
          supabase
            .from('expert_chat_messages')
            .select('id', { count: 'exact', head: true })
            .eq('sender_type', 'user'),
          supabase
            .from('user_lesson_progress')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id),
        ]);

        const scriptCount = scriptUsageResult.count ?? 0;
        const chatCount = chatMessagesResult.count ?? 0;
        const lessonCount = lessonProgressResult.count ?? 0;
        const hasAction = scriptCount > 0 || chatCount > 0 || lessonCount > 0;

        logger.log('[Feedback] Eligibility:', { scriptCount, chatCount, lessonCount, hasAction });

        if (!hasAction) return;

        // Onda 3 (21/09/2026): no dia em que a pergunta "worth what you paid"
        // aparece, este survey não aparece. Duas perguntas no mesmo dia é ruído.
        // Duas fontes: a marca de sessão (síncrona, cobre a corrida entre os
        // dois instrumentos) e a linha no banco (cobre outro aparelho). Erro na
        // leitura não é autorização: sem resposta, este survey espera o dia.
        const hoje = new Date().toISOString().slice(0, 10);
        if (readSessionFlag(PROMPT_SHOWN_KEY) === hoje) {
          logger.log('[Feedback] Eligibility:', { status: 'purchase value prompt shown today (session)' });
          return;
        }
        const { data: valuePrompt, error: valuePromptError } = await supabase
          .from('purchase_value_answers')
          .select('shown_at')
          .eq('user_id', user.id)
          .maybeSingle();
        if (valuePromptError) {
          logger.warn('[Feedback] purchase_value_answers read failed; skipping survey today', valuePromptError);
          return;
        }
        if (valuePrompt?.shown_at && valuePrompt.shown_at.slice(0, 10) === hoje) {
          logger.log('[Feedback] Eligibility:', { status: 'purchase value prompt shown today' });
          return;
        }

        // Step 3: Check existing feedback record
        const { data: existing, error: feedbackError } = await supabase
          .from('user_feedback')
          .select('id, status, dismiss_count, dismissed_at, first_shown_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (feedbackError) {
          logger.error('[Feedback] Error checking feedback record:', feedbackError);
          return;
        }

        // Step 4: If completed, don't show
        if (existing?.status === 'completed') {
          logger.log('[Feedback] Eligibility:', { status: 'already completed' });
          return;
        }

        // Step 5: If dismissed, check retry conditions
        if (existing?.status === 'dismissed') {
          const dismissCount = existing.dismiss_count ?? 0;
          const dismissedAt = existing.dismissed_at ? new Date(existing.dismissed_at).getTime() : 0;
          const now = Date.now();

          if (dismissCount >= MAX_DISMISS_COUNT) {
            logger.log('[Feedback] Eligibility:', { status: 'max dismissals reached', dismissCount });
            return;
          }

          if (dismissedAt + DISMISS_RETRY_MS > now) {
            logger.log('[Feedback] Eligibility:', { status: 'too soon after dismiss', dismissedAt, retryAfter: dismissedAt + DISMISS_RETRY_MS });
            return;
          }
        }

        // Step 6: Insert or update to pending status
        if (existing) {
          await supabase
            .from('user_feedback')
            .update({ status: 'pending', first_shown_at: existing.first_shown_at || new Date().toISOString() })
            .eq('id', existing.id);
        } else {
          await supabase
            .from('user_feedback')
            .insert({
              user_id: user.id,
              status: 'pending',
              first_shown_at: new Date().toISOString(),
              dismiss_count: 0,
            });
        }

        writeSessionFlag(SURVEY_OPEN_KEY, '1');
        setShouldShowInvite(true);
        logger.log('[Feedback] Showing invite:', { userId: user.id });
      } catch (err) {
        logger.error('[Feedback] Eligibility check error:', err);
      }
    };

    checkEligibility();
  }, [user?.id]);

  // --- Actions ---

  const accept = useCallback(() => {
    setShouldShowInvite(false);
    setShouldShowSurvey(true);
  }, []);

  const dismiss = useCallback(async () => {
    setShouldShowInvite(false);
    setShouldShowSurvey(false);

    if (!user?.id) return;

    try {
      const { data: existing } = await supabase
        .from('user_feedback')
        .select('id, dismiss_count')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        const newCount = (existing.dismiss_count ?? 0) + 1;
        await supabase
          .from('user_feedback')
          .update({
            status: 'dismissed',
            dismiss_count: newCount,
            dismissed_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      }

      logger.log('[Feedback] Dismissed:', { userId: user.id });
    } catch (err) {
      logger.error('[Feedback] Dismiss error:', err);
    }
  }, [user?.id]);

  const submit = useCallback(
    async (data: FeedbackData) => {
      if (!user?.id) return;

      setIsLoading(true);
      try {
        // 25/09/2026 (grill com o Astra): o supabase não lança; o erro e o
        // "zero linhas atualizadas" passavam em silêncio e a tela agradecia.
        const payload = {
            satisfaction: data.satisfaction,
            purchase_experience: data.purchase_experience,
            purchase_details: data.purchase_details || null,
            feature_requests: data.feature_requests,
            open_feedback: data.open_feedback || null,
            word_count: data.word_count,
            early_access_unlocked: data.word_count >= 30,
            status: 'completed',
            completed_at: new Date().toISOString(),
        };
        const { data: updated, error: updateError } = await supabase
          .from('user_feedback')
          .update(payload)
          .eq('user_id', user.id)
          .select('id');
        if (updateError) throw updateError;
        if (!updated || updated.length === 0) {
          const { error: insertError } = await supabase
            .from('user_feedback')
            .insert({ user_id: user.id, ...payload });
          if (insertError) throw insertError;
        }

        // Award "Early Supporter" badge via upsert
        await supabase
          .from('user_badges')
          .upsert(
            {
              user_id: user.id,
              badge_id: EARLY_SUPPORTER_BADGE_ID,
              unlocked_at: new Date().toISOString(),
            },
            { onConflict: 'user_id,badge_id' }
          );

        setShouldShowSurvey(false);
        logger.log('[Feedback] Submitted:', { userId: user.id, wordCount: data.word_count, earlyAccess: data.word_count >= 30 });
      } catch (err) {
        logger.error('[Feedback] Submit error:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id]
  );

  return {
    shouldShowInvite,
    shouldShowSurvey,
    accept,
    dismiss,
    submit,
    isLoading,
  };
}
